import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../Store/store';
import { motion } from 'framer-motion';
import { BasePageProps } from '../../Page/PageTypes';
import { ButtonStylized, ButtonStylizedType } from '../../ButtonStylized/ButtonStylized';
import AnimatedCounter from '../../Animated/AnimatedCounter';
import * as Lucide from 'lucide-react';
import { LocalSaveManager, logger } from '@mental-mint/shared-utils';

/**
 * Props for the PageDailyStreak component.
 * Extends BasePageProps with streak-specific props.
 */
interface PageDailyStreakProps extends BasePageProps {
  /** Player ID for saving modal state */
  playerId: string;
  /** Current streak value */
  currentStreak: number;
  /** Optional initial streak for animation */
  initialStreak?: number;
  /** Whether to auto-show the modal */
  autoShow?: boolean;
}

/**
 * Daily streak celebration page component.
 * Can be used as a modal page by any game that wants to show streak celebrations.
 * Extracted from Witocracy's StreakCongratulationsModal for reuse across games.
 */
export const PageDailyStreak: React.FC<PageDailyStreakProps> = ({
  onRequestClose,
  playerId,
  currentStreak,
  initialStreak = 0,
  autoShow = false
}) => {
  // Get game data from Redux
  const { currentGame: gameSettings } = useSelector((state: RootState) => state.gameSession);
  
  // Initialize all hooks before any early returns
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    if (autoShow) {
      // Delay the animation start slightly for better effect
      const timer = setTimeout(() => {
        setShowAnimation(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setShowAnimation(true);
    }
  }, [autoShow]);

  // Early return if no game data
  if (!gameSettings) {
    return <div>Loading...</div>;
  }

  // Get today's date for save key
  const getTodayDateString = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Mark that we've shown the streak modal today
  const markStreakModalShownToday = () => {
    if (!gameSettings) return;
    
    const saveKey = `streakModalShown_${playerId}`;
    LocalSaveManager.saveSimpleData(gameSettings.metadata.gameId, saveKey, {
      date: getTodayDateString(),
      streak: currentStreak
    });
  };

  const handleClose = () => {
    markStreakModalShownToday();
    if (onRequestClose) {
      onRequestClose();
    }
  };

  if (!gameSettings) {
    return <div>Error: Game settings required for daily streak page</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1.1rem',
        padding: '1rem',
        textAlign: 'center',
        minHeight: '400px',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 10000, // High z-index to ensure it appears on top
      }}
    >
      {/* Celebration Icon */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ duration: 0.8, delay: 0.2, type: "spring", bounce: 0.5 }}
      >
        <Lucide.Flame 
          size={80} 
          strokeWidth={2}
          style={{ 
            color: '#FF6B35',
            filter: 'drop-shadow(0 0 20px rgba(255, 107, 53, 0.6))'
          }}
        />
      </motion.div>

      {/* Congratulations Text */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7 }}
        style={{
          color: 'white',
          fontFamily: gameSettings.fontPrimary,
        }}
      >
        <h2 style={{ 
          fontSize: '2.2rem', 
          margin: '0 0 0.5rem 0',
          fontWeight: 'bold',
          textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
        }}>
          Streak Extended! 🎉
        </h2>
        <p style={{ 
          fontSize: '1.2rem', 
          margin: 0,
          opacity: 0.9
        }}>
          Your daily streak has grown to:
        </p>
      </motion.div>

      {/* Animated Streak Counter */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 1.2, type: "spring", bounce: 0.3 }}
        style={{
          background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.2), rgba(255, 165, 0, 0.2))',
          borderRadius: '20px',
          padding: '2rem',
          border: '3px solid rgba(255, 107, 53, 0.5)',
          boxShadow: '0 0 30px rgba(255, 107, 53, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}
      >
        <Lucide.Flame 
          size={40} 
          strokeWidth={3}
          style={{ color: '#FF6B35' }}
        />
        {showAnimation ? (
          <AnimatedCounter
            start={initialStreak}
            end={currentStreak}
            duration={0.5}
            delay={0.6}
            fontSize="4rem"
            additionalStyles={{
              fontWeight: 'bold',
              color: '#FF6B35',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              fontFamily: gameSettings.fontPrimary
            }}
          />
        ) : (
          <span style={{
            fontSize: '4rem',
            fontWeight: 'bold',
            color: '#FF6B35',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            fontFamily: gameSettings.fontPrimary
          }}>
            {currentStreak}
          </span>
        )}
        <span style={{
          fontSize: '1.5rem',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {currentStreak === 1 ? 'day' : 'days'}
        </span>
      </motion.div>

      {/* Motivational Message */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 2.1 }}
        style={{
          color: 'rgba(255, 255, 255, 0.8)',
          fontSize: '1rem',
          fontStyle: 'italic',
          maxWidth: '300px'
        }}
      >
        {currentStreak === 30 ? "One month! You're a true champion! 🏆" :
         currentStreak === 7 ? "One week! Bravo! 🏆" :
         currentStreak >= 4 ? "Well done. Keep the momentum going! 🚀" :
         currentStreak >= 3 ?  "Three in a row, you're killing it! 🎉" :
         currentStreak >= 2 ? "Two in a row, keep it up! 🙌" :
         "Great start! Come back tomorrow ⭐"}
      </motion.div>

      {/* Close Button */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 2.1 }}
      >
        <ButtonStylized
          buttonType={ButtonStylizedType.Primary}
          text="Awesome!"
          icon={<Lucide.Check size={24} strokeWidth={3} />}
          onClick={handleClose}
          additionalStyles={{ 
            fontSize: '1.2rem',
            padding: '0.8rem 2rem',
            background: 'linear-gradient(135deg, #FF6B35, #FF8E53)',
            border: 'none',
            boxShadow: '0 4px 15px rgba(255, 107, 53, 0.4)'
          }}
        />
      </motion.div>
    </motion.div>
  );
};

/**
 * Helper function to check if streak modal should be shown for a player.
 * Can be used by games to determine if they should show the daily streak page.
 */
export const shouldShowDailyStreakPage = (
  currentStreak: number,
  playerId: string,
  gameSettings: any
): boolean => {
  logger.debug(`Checking if daily streak page should be shown for player ${playerId}: currentStreak=${currentStreak}`);
  
  // Don't show if no streak
  if (currentStreak <= 0) {
    return false;
  }

  // Check if we've already shown the modal today
  const saveKey = `streakModalShown_${playerId}`;
  const todayDateString = new Date().toISOString().split('T')[0];
  
  const savedData = LocalSaveManager.loadSimpleData<{ date?: string; streak?: number }>(gameSettings.metadata.gameId, saveKey);
  if (savedData?.data?.date === todayDateString) {
    logger.debug(`Daily streak page already shown today for player ${playerId}`);
    return false; // Already shown today
  }

  return true;
};
